import { useEffect, useRef, useState, useCallback, FormEvent } from "react";
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Camera, ImageOff, CloudUpload } from "lucide-react";
import { api, mensajeError } from "../../api/client";
import { Modal } from "../ui/Modal";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { PageHeader } from "../ui/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { redimensionarImagenABase64 } from "../../lib/imagen";

export interface CampoFormulario {
  name: string;
  label: string;
  type?: "text" | "number" | "date" | "datetime-local" | "select" | "textarea" | "checkbox" | "email" | "tel" | "image" | "custom";
  options?: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  colSpan2?: boolean;
  default?: any;
  // Para type "custom": dibuja lo que sea en vez del input de siempre (por
  // ejemplo, un grupo de domicilio con varios campos y autocompletado). Se
  // le pasan los valores actuales del formulario completo y una función
  // para actualizar un campo por nombre.
  render?: (valores: Record<string, any>, onChange: (campo: string, valor: any) => void) => React.ReactNode;
  // Para campos que ya se dibujan dentro de un "custom" de otro campo (ej.
  // numero/colonia/ciudad dentro del "custom" de calle): siguen recibiendo
  // su valor inicial y viajando en el payload igual que cualquier otro
  // campo, solo que no dibujan su propia cajita en el formulario.
  ocultarEnFormulario?: boolean;
  // Para type "image": permite ajustar el tamaño/calidad de compresión por
  // campo (ej. documentos escaneados necesitan verse más nítidos que una
  // foto de perfil). Si no se especifican, se usan los valores por default
  // (500px de lado máximo, calidad 0.85).
  imagenLadoMaximo?: number;
  imagenCalidad?: number;
}

// Convierte un ISO en UTC (lo que regresa el backend) a la cadena
// "YYYY-MM-DDTHH:mm" en hora LOCAL del navegador que espera un
// <input type="datetime-local">.
function aFechaLocalInput(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Lo mismo pero para <input type="date">, que solo acepta "YYYY-MM-DD". Si
// se le pasa el ISO completo tal cual (ej. "2026-07-18T00:00:00.000Z"), el
// navegador simplemente lo rechaza y deja el campo en blanco — por eso las
// fechas "desaparecían" al editar un registro con un campo type="date".
export function aFechaSoloDiaInput(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface ColumnaTabla<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
}

interface CrudPageProps<T extends { id: string }> {
  resource: string;
  titulo: string;
  descripcion?: string;
  // Ícono temático de la sección (ver PageHeader) — opcional, si no se pasa
  // el encabezado se ve igual que siempre.
  icono?: React.ReactNode;
  campos: CampoFormulario[];
  columnas: ColumnaTabla<T>[];
  nombreSingular?: string;
  onRowClick?: (row: T) => void;
  transformarAntesDeEnviar?: (valores: Record<string, any>) => Record<string, any>;
  accionesExtra?: React.ReactNode;
  permitirCrear?: boolean;
  permitirEditar?: boolean;
  permitirEliminar?: boolean;
  filtrosFijos?: Record<string, string>;
  // Si se da, reemplaza por completo el flujo de "eliminar" (confirm() +
  // DELETE genérico) por esta función — útil cuando el borrado necesita un
  // flujo propio (ej. pedir un motivo antes de dar de baja una mascota). El
  // padre es responsable de mostrar su propio modal/confirmación y de llamar
  // a `recargarRef.current?.()` cuando termine, para refrescar la tabla.
  onEliminarPersonalizado?: (row: T) => void;
  // El padre puede pasar un ref vacío aquí; CrudPage lo llena con su función
  // de recarga, para poder refrescar la tabla desde fuera (ver arriba).
  recargarRef?: React.MutableRefObject<(() => void) | null>;
  // Sección del panel (ver frontend/src/lib/permisos.ts) que gobierna esta
  // página. Si el rol del usuario actual solo tiene "VER" en esta sección,
  // los botones de Nuevo/Editar/Eliminar se ocultan aunque permitirCrear/
  // permitirEditar/permitirEliminar sean true — sin esto la página se
  // comporta igual que siempre (compatibilidad hacia atrás).
  seccion?: string;
  // "tabla" (default, igual que siempre) o "tarjetas" — una cuadrícula de
  // tarjetas en vez de filas, para listados donde una vista más visual ayuda
  // a que se note que cada elemento se puede abrir (ej. Mascotas). Requiere
  // pasar también `renderTarjeta`; si no se pasa, se usa "tabla" aunque se
  // indique "tarjetas".
  vista?: "tabla" | "tarjetas";
  // Dibuja el contenido de una tarjeta individual en la vista "tarjetas". No
  // necesita manejar el clic para abrir el detalle (eso ya lo hace el
  // contenedor de la tarjeta con `onRowClick`) ni el estado "pendiente de
  // sincronizar" (CrudPage ya le pone una franja encima automáticamente) —
  // solo el contenido. Para los botones de editar/eliminar dentro de la
  // tarjeta, usa las funciones de `acciones` y llama a
  // `e.stopPropagation()` en su onClick para que no dispare `onRowClick`.
  renderTarjeta?: (
    row: T,
    acciones: { puedeEditar: boolean; puedeEliminar: boolean; onEditar: () => void; onEliminar: () => void }
  ) => React.ReactNode;
}

export function CrudPage<T extends { id: string }>({
  resource,
  titulo,
  descripcion,
  icono,
  campos,
  columnas,
  nombreSingular = "registro",
  onRowClick,
  transformarAntesDeEnviar,
  accionesExtra,
  permitirCrear = true,
  permitirEditar = true,
  permitirEliminar = true,
  filtrosFijos,
  onEliminarPersonalizado,
  recargarRef,
  seccion,
  vista = "tabla",
  renderTarjeta,
}: CrudPageProps<T>) {
  const usarTarjetas = vista === "tarjetas" && !!renderTarjeta;
  const { soloLectura, permisoSeccion } = useAuth();
  const soloVer = !!seccion && permisoSeccion(seccion) === "VER";
  const [items, setItems] = useState<T[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<T | null>(null);
  const [valores, setValores] = useState<Record<string, any>>({});
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState<string | null>(null);
  const [subiendoImagen, setSubiendoImagen] = useState<string | null>(null);
  const inputsImagen = useRef<Record<string, HTMLInputElement | null>>({});

  const puedeCrear = permitirCrear && !soloLectura && !soloVer;
  const puedeEditar = permitirEditar && !soloLectura && !soloVer;
  const puedeEliminar = permitirEliminar && !soloLectura && !soloVer;

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await api.get(resource, {
        params: { pagina, porPagina: 20, buscar: buscar || undefined, ...filtrosFijos },
      });
      setItems(res.data.items ?? []);
      setTotalPaginas(res.data.totalPaginas ?? 1);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setCargando(false);
    }
  }, [resource, pagina, buscar, JSON.stringify(filtrosFijos)]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (recargarRef) recargarRef.current = cargar;
  }, [recargarRef, cargar]);

  // Cuando algo se sincroniza de verdad (o se encola algo nuevo) sin
  // conexión, se refresca la lista — así el registro "pendiente" que se
  // agregó a la vista localmente se reemplaza por el real en cuanto el
  // servidor ya lo tiene guardado.
  useEffect(() => {
    window.addEventListener("mascotasvet:pendientes-cambiaron", cargar);
    return () => window.removeEventListener("mascotasvet:pendientes-cambiaron", cargar);
  }, [cargar]);

  function abrirCrear() {
    const iniciales: Record<string, any> = { ...filtrosFijos };
    campos.forEach((c) => (iniciales[c.name] = c.default ?? (c.type === "checkbox" ? false : "")));
    setValores(iniciales);
    setEditando(null);
    setErrorForm(null);
    setModalAbierto(true);
  }

  function abrirEditar(row: T) {
    const iniciales: Record<string, any> = {};
    campos.forEach((c) => {
      let v = (row as any)[c.name];
      // El valor que regresa el backend es un ISO en UTC (ej. "...T21:00:00.000Z").
      // Un <input type="datetime-local"> necesita "YYYY-MM-DDTHH:mm" en hora
      // LOCAL del navegador, o el campo se queda en blanco al editar.
      if (c.type === "datetime-local" && v) {
        v = aFechaLocalInput(v);
      } else if (c.type === "date" && v) {
        v = aFechaSoloDiaInput(v);
      }
      iniciales[c.name] = v ?? (c.type === "checkbox" ? false : "");
    });
    setValores(iniciales);
    setEditando(row);
    setErrorForm(null);
    setModalAbierto(true);
  }

  async function elegirImagen(campo: string, e: React.ChangeEvent<HTMLInputElement>, ladoMaximo = 500, calidad?: number) {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo) return;
    setSubiendoImagen(campo);
    try {
      const base64 = await redimensionarImagenABase64(archivo, ladoMaximo, calidad);
      setValores((v) => ({ ...v, [campo]: base64 }));
    } catch (err) {
      setErrorForm(mensajeError(err));
    } finally {
      setSubiendoImagen(null);
    }
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setErrorForm(null);
    try {
      // Los inputs datetime-local entregan una cadena "naive" en hora local
      // del navegador (sin zona horaria), p. ej. "2026-07-18T10:00". Si se
      // manda tal cual, el backend (que corre en UTC) la interpreta como si
      // ya fuera UTC y la hora se recorre varias horas. Hay que convertirla
      // explícitamente a un instante absoluto (ISO con offset) antes de
      // enviarla.
      const conFechasCorregidas = { ...valores };
      for (const c of campos) {
        if (c.type === "datetime-local" && conFechasCorregidas[c.name]) {
          conFechasCorregidas[c.name] = new Date(conFechasCorregidas[c.name]).toISOString();
        }
        // Un campo de fecha opcional dejado en blanco llega como "" — el
        // backend lo rechaza (no es una fecha válida), así que se omite del
        // payload en vez de mandarlo vacío.
        if ((c.type === "date" || c.type === "datetime-local") && !c.required && !conFechasCorregidas[c.name]) {
          delete conFechasCorregidas[c.name];
        }
      }
      const payload = transformarAntesDeEnviar ? transformarAntesDeEnviar(conFechasCorregidas) : conFechasCorregidas;
      let pendienteSync = false;
      if (editando) {
        const res = await api.put(`${resource}/${editando.id}`, payload);
        pendienteSync = !!res.data?._pendienteSync;
      } else {
        const res = await api.post(resource, payload);
        pendienteSync = !!res.data?._pendienteSync;
      }
      setModalAbierto(false);
      if (pendienteSync) {
        if (editando) {
          const idEditando = editando.id;
          setItems((prev) =>
            prev.map((it) => ((it as any).id === idEditando ? ({ ...it, ...payload, _pendienteSync: true } as T) : it))
          );
        } else {
          const registroOptimista = { ...payload, id: `_pendiente_${Date.now()}`, _pendienteSync: true } as unknown as T;
          setItems((prev) => [registroOptimista, ...prev]);
        }
      } else {
        cargar();
      }
    } catch (err) {
      setErrorForm(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  async function eliminar(row: T) {
    if (!confirm(`¿Seguro que quieres eliminar este ${nombreSingular}? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await api.delete(`${resource}/${row.id}`);
      if (res.data?._pendienteSync) {
        setItems((prev) => prev.map((it) => ((it as any).id === row.id ? ({ ...it, _pendienteEliminar: true } as T) : it)));
      } else {
        cargar();
      }
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  return (
    <div>
      <PageHeader
        titulo={titulo}
        descripcion={descripcion}
        icono={icono}
        acciones={
          <>
            {accionesExtra}
            {puedeCrear && (
              <button className="btn-primary" onClick={abrirCrear}>
                <Plus size={16} /> Nuevo {nombreSingular}
              </button>
            )}
          </>
        }
      />

      <div className="card">
        <div className="flex items-center gap-2 border-b border-slate-100 p-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Buscar..."
              value={buscar}
              onChange={(e) => {
                setPagina(1);
                setBuscar(e.target.value);
              }}
            />
          </div>
        </div>

        {cargando ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : items.length === 0 ? (
          <EmptyState titulo={`No hay ${titulo.toLowerCase()} todavía`} descripcion="Crea el primero usando el botón de arriba." />
        ) : usarTarjetas ? (
          <>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((row) => {
                const pendienteSync = !!(row as any)._pendienteSync;
                const pendienteEliminar = !!(row as any)._pendienteEliminar;
                return (
                  <div
                    key={row.id}
                    className={`entrada-contenido group relative ${onRowClick ? "cursor-pointer" : ""} ${
                      pendienteSync || pendienteEliminar ? "rounded-2xl ring-2 ring-amber-200" : ""
                    }`}
                    onClick={() => onRowClick?.(row)}
                  >
                    {(pendienteSync || pendienteEliminar) && (
                      <span
                        title={
                          pendienteEliminar
                            ? "Se eliminará en cuanto regrese la conexión"
                            : "Guardado localmente — se enviará al servidor en cuanto regrese la conexión"
                        }
                        className="absolute -top-2 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 shadow-sm"
                      >
                        <CloudUpload size={10} /> {pendienteEliminar ? "Eliminando..." : "Pendiente"}
                      </span>
                    )}
                    {renderTarjeta!(row, {
                      puedeEditar,
                      puedeEliminar,
                      onEditar: () => abrirEditar(row),
                      onEliminar: () => (onEliminarPersonalizado ? onEliminarPersonalizado(row) : eliminar(row)),
                    })}
                  </div>
                );
              })}
            </div>

            {totalPaginas > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                <span className="text-xs text-slate-400">Página {pagina} de {totalPaginas}</span>
                <div className="flex gap-2">
                  <button className="btn-secondary !px-2" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                    <ChevronLeft size={16} />
                  </button>
                  <button className="btn-secondary !px-2" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                    {columnas.map((c) => (
                      <th key={c.key} className="px-4 py-3 font-medium">{c.label}</th>
                    ))}
                    <th className="px-4 py-3 text-right font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, i) => {
                    const pendienteSync = !!(row as any)._pendienteSync;
                    const pendienteEliminar = !!(row as any)._pendienteEliminar;
                    return (
                    <tr
                      key={row.id ?? i}
                      className={`border-b border-slate-50 last:border-0 hover:bg-slate-50 ${onRowClick ? "cursor-pointer" : ""} ${
                        pendienteSync || pendienteEliminar ? "bg-amber-50/60" : ""
                      }`}
                      onClick={() => onRowClick?.(row)}
                    >
                      {columnas.map((c, ci) => (
                        <td key={c.key} className="px-4 py-3 text-slate-700">
                          {ci === 0 && (pendienteSync || pendienteEliminar) && (
                            <span
                              title={
                                pendienteEliminar
                                  ? "Se eliminará en cuanto regrese la conexión"
                                  : "Guardado localmente — se enviará al servidor en cuanto regrese la conexión"
                              }
                              className="mr-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 align-middle"
                            >
                              <CloudUpload size={10} /> {pendienteEliminar ? "Eliminando..." : "Pendiente"}
                            </span>
                          )}
                          {c.render ? c.render(row) : (row as any)[c.key]}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {puedeEditar && !pendienteEliminar && (
                            <button className="btn-ghost !px-2" title="Editar" onClick={() => abrirEditar(row)}>
                              <Pencil size={15} />
                            </button>
                          )}
                          {puedeEliminar && !pendienteEliminar && (
                            <button
                              className="btn-ghost !px-2 text-red-500"
                              title="Eliminar"
                              onClick={() => (onEliminarPersonalizado ? onEliminarPersonalizado(row) : eliminar(row))}
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                <span className="text-xs text-slate-400">Página {pagina} de {totalPaginas}</span>
                <div className="flex gap-2">
                  <button className="btn-secondary !px-2" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                    <ChevronLeft size={16} />
                  </button>
                  <button className="btn-secondary !px-2" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal open={modalAbierto} onClose={() => setModalAbierto(false)} title={editando ? `Editar ${nombreSingular}` : `Nuevo ${nombreSingular}`}>
        <form onSubmit={guardar} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {campos.map((c) => {
              if (c.ocultarEnFormulario) return null;
              if (c.type === "custom") {
                return (
                  <div key={c.name} className={c.colSpan2 ? "col-span-2" : ""}>
                    {c.render!(valores, (campo, valor) => setValores((v) => ({ ...v, [campo]: valor })))}
                  </div>
                );
              }
              return (
              <div key={c.name} className={c.colSpan2 ? "col-span-2" : ""}>
                <label className="label">
                  {c.label}
                  {c.required && <span className="text-red-500"> *</span>}
                </label>
                {c.type === "image" ? (
                  <div className="flex items-center gap-3">
                    <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
                      {valores[c.name] ? (
                        <img src={valores[c.name]} alt={c.label} className="h-full w-full object-cover" />
                      ) : (
                        <ImageOff size={20} className="text-slate-300" />
                      )}
                    </div>
                    <div>
                      <input
                        ref={(el) => (inputsImagen.current[c.name] = el)}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => elegirImagen(c.name, e, c.imagenLadoMaximo, c.imagenCalidad)}
                      />
                      <button
                        type="button"
                        className="btn-secondary !py-1.5 !text-xs"
                        disabled={subiendoImagen === c.name}
                        onClick={() => inputsImagen.current[c.name]?.click()}
                      >
                        <Camera size={14} />
                        {subiendoImagen === c.name ? "Procesando..." : valores[c.name] ? "Cambiar" : "Tomar/subir foto"}
                      </button>
                      {valores[c.name] && (
                        <button
                          type="button"
                          className="ml-2 text-xs text-red-500 hover:underline"
                          onClick={() => setValores({ ...valores, [c.name]: "" })}
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                  </div>
                ) : c.type === "select" ? (
                  <select
                    className="input"
                    required={c.required}
                    value={valores[c.name] ?? ""}
                    onChange={(e) => setValores({ ...valores, [c.name]: e.target.value })}
                  >
                    <option value="">Selecciona...</option>
                    {c.options?.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : c.type === "textarea" ? (
                  <textarea
                    className="input"
                    rows={3}
                    required={c.required}
                    placeholder={c.placeholder}
                    value={valores[c.name] ?? ""}
                    onChange={(e) => setValores({ ...valores, [c.name]: e.target.value })}
                  />
                ) : c.type === "checkbox" ? (
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
                    checked={!!valores[c.name]}
                    onChange={(e) => setValores({ ...valores, [c.name]: e.target.checked })}
                  />
                ) : (
                  <input
                    type={c.type ?? "text"}
                    className="input"
                    required={c.required}
                    placeholder={c.placeholder}
                    value={valores[c.name] ?? ""}
                    onChange={(e) => setValores({ ...valores, [c.name]: e.target.value })}
                  />
                )}
              </div>
              );
            })}
          </div>

          {errorForm && <p className="text-sm text-red-600">{errorForm}</p>}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={() => setModalAbierto(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardando}>
              {guardando && <Spinner className="h-4 w-4 text-white" />}
              Guardar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
